import { createSelector } from 'reselect';
import { IDeathsState } from 'types/deaths.types';
import { IDeathsFormData } from 'views/public/DeathsView/components/DeathsForm/types';

const getItems = (state: { deaths: IDeathsState }) => state.deaths.items;
const getSelectedItem = (state: { deaths: IDeathsState }) => state.deaths.selectedItem;
const getCementeries = (state: { deaths: IDeathsState }) => state.deaths.cementeries;
const getCivilStates = (state: { deaths: IDeathsState }) => state.deaths.civilStates;
const getIdCardTypes = (state: { deaths: IDeathsState }) => state.deaths.idCardTypes;
const getTribes = (state: { deaths: IDeathsState }) => state.deaths.tribes;

export const deathsSelector = createSelector([getItems], (deaths) => deaths);

export const deathsFormSelector = createSelector(
  [getSelectedItem],
  (death): IDeathsFormData => ({
    person: {
      id: death?.person?.id || 0,
      name: death?.person?.name || '',
      lastName: death?.person?.lastName || '',
      idCardType: death?.person?.idCardType,
      idCard: death?.person?.idCard || '',
      age: death?.person?.age || '',
      country: death?.person?.country || '',
      address: death?.person?.address || '',
      birthdate: death?.person?.birthdate || '',
      deathDate: death?.person?.deathDate || '',
      deathPlace: death?.person?.deathPlace || '',
      deathDateHebrew: death?.person?.deathDateHebrew || '',
      community: death?.person?.community || '',
      civilState: death?.person?.civilState || '',
      tribe: death?.person?.tribe || '',
    },
    spouse: {
      id: death?.spouse?.id || 0,
      name: death?.spouse?.name || '',
      lastName: death?.spouse?.lastName || '',
      weddingTemple: death?.spouse?.weddingTemple || '',
      alive: death?.spouse?.alive ?? true,
      cementery: death?.spouse?.cementery || '',
    },
    family: {
      id: death?.family?.id || 0,
      fatherInfo: death?.family?.fatherInfo || '',
      motherInfo: death?.family?.motherInfo || '',
      childrenInfo: death?.family?.childrenInfo || '',
      cementeries: death?.family?.cementeries || '',
    },
    location: {
      id: death?.location?.id || 0,
      cementery: death?.location?.cementery || '',
      sector: death?.location?.sector || '',
      row: death?.location?.row || '',
      plank: death?.location?.plank || '',
      grave: death?.location?.grave || '',
      block: death?.location?.block || '',
    },
    signers: [
      {
        id: death?.signers?.[0]?.id || 0,
        name: death?.signers?.[0]?.name || '',
        idCardType: death?.signers?.[0]?.idCardType,
        idCard: death?.signers?.[0]?.idCard || '',
        address: death?.signers?.[0]?.address || '',
        areaCode: death?.signers?.[0]?.areaCode || '',
        phone: death?.signers?.[0]?.phone || '',
        email: death?.signers?.[0]?.email || '',
        telephone: death?.signers?.[0]?.telephone || '',
        kinship: death?.signers?.[0]?.kinship || '',
      },
      {
        id: death?.signers?.[1]?.id || 0,
        name: death?.signers?.[1]?.name || '',
        idCardType: death?.signers?.[1]?.idCardType,
        idCard: death?.signers?.[1]?.idCard || '',
        address: death?.signers?.[1]?.address || '',
        areaCode: death?.signers?.[1]?.areaCode || '',
        phone: death?.signers?.[1]?.phone || '',
        email: death?.signers?.[1]?.email || '',
        telephone: death?.signers?.[1]?.telephone || '',
        kinship: death?.signers?.[1]?.kinship || '',
      },
      {
        id: death?.signers?.[2]?.id || 0,
        name: death?.signers?.[2]?.name || '',
        idCardType: death?.signers?.[2]?.idCardType,
        idCard: death?.signers?.[2]?.idCard || '',
        address: death?.signers?.[2]?.address || '',
        areaCode: death?.signers?.[2]?.areaCode || '',
        phone: death?.signers?.[2]?.phone || '',
        email: death?.signers?.[2]?.email || '',
        telephone: death?.signers?.[2]?.telephone || '',
        kinship: death?.signers?.[2]?.kinship || '',
      },
      {
        id: death?.signers?.[3]?.id || 0,
        name: death?.signers?.[3]?.name || '',
        idCardType: death?.signers?.[3]?.idCardType,
        idCard: death?.signers?.[3]?.idCard || '',
        address: death?.signers?.[3]?.address || '',
        areaCode: death?.signers?.[3]?.areaCode || '',
        phone: death?.signers?.[3]?.phone || '',
        email: death?.signers?.[3]?.email || '',
        telephone: death?.signers?.[3]?.telephone || '',
        kinship: death?.signers?.[3]?.kinship || '',
      },
    ],
    signer: {
      id: death?.signer?.id || 0,
      name: death?.signer?.name || '',
      idCardType: death?.signer?.idCardType,
      idCard: death?.signer?.idCard || '',
      address: death?.signer?.address || '',
      areaCode: death?.signer?.areaCode || '',
      phone: death?.signer?.phone || '',
      relationship: death?.signer?.relationship || '',
      kinship: death?.signer?.kinship || '',
    },
    payment: {
      id: death?.payment?.id || 0,
      currency: death?.payment?.currency || '',
      amount: death?.payment?.amount || '',
      paymentConditions: death?.payment?.paymentConditions || '',
      paymentsDone: death?.payment?.paymentsDone || '',
      garage: death?.payment?.garage || '',
    },
    comments: death?.comments || '',
  }),
);

export const deathsCementeriesSelector = createSelector([getCementeries], (cementeries) =>
  Object.keys(cementeries).map((key) => ({
    label: cementeries[key],
    value: key,
  })),
);

export const deathsCivilStatesSelector = createSelector([getCivilStates], (civilStates) =>
  Object.keys(civilStates).map((key) => ({
    label: civilStates[key],
    value: key,
  })),
);

export const deathsIdCardTypesSelector = createSelector([getIdCardTypes], (idCardTypes) =>
  Object.keys(idCardTypes).map((key) => ({
    label: idCardTypes[key],
    value: key,
  })),
);

export const deathsTribesSelector = createSelector([getTribes], (tribes) =>
  Object.keys(tribes).map((key) => ({
    label: tribes[key],
    value: key,
  })),
);

export const deathsFilesSelector = createSelector([getSelectedItem], (death) => ({
  personIdCardFile: death?.person?.idCardFile || '',
  personIdCardBackFile: death?.person?.idCardBackFile || '',
  personKetubaFile: death?.person?.ketubaFile || '',
  personMarriageCertificateFile: death?.person?.marriageCertificateFile || '',
  personGetFile: death?.person?.getFile || '',
  signerIdCardFile: death?.signer?.idCardFile || '',
  signerIdCardBackFile: death?.signer?.idCardBackFile || '',
  signersIdCardFiles: death?.signers?.map((signer) => signer.idCardFile) || [],
  signersIdCardBackFiles: death?.signers?.map((signer) => signer.idCardBackFile) || [],
}));
